"use client"

import { useState, useEffect } from "react"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Badge } from "@/components/ui/badge"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs"
import { Sparkles, ArrowRight, MapPin, Briefcase, Calendar, Building, Code, Check } from "lucide-react"
import { EditFiltersDialog } from "@/components/edit-filters-dialog"
import {
  buildFilterSummary,
  DEFAULT_SEARCH_FILTERS_DRAFT,
  type SearchFiltersDraft,
} from "@/lib/search-filter-model"
import type { SearchFilters } from "@/types/job-search"

interface JobSearchFormProps {
  initialFilters?: SearchFilters
  onSearch?: (query: string, booleanQuery: string, filters: SearchFiltersDraft) => void
}

export function JobSearchForm({ initialFilters, onSearch }: JobSearchFormProps) {
  const [searchQuery, setSearchQuery] = useState(initialFilters?.searchTerm || "")
  const [jobDescription, _setJobDescription] = useState("")
  const [isAnalyzing, _setIsAnalyzing] = useState(false)
  const [booleanQuery, _setBooleanQuery] = useState("")
  const [activeTab, setActiveTab] = useState("natural")
  const [filters, setFilters] = useState<SearchFiltersDraft>(DEFAULT_SEARCH_FILTERS_DRAFT)
  const summaryItems = buildFilterSummary(filters)

  useEffect(() => {
    if (initialFilters) {
      setFilters({
        ...DEFAULT_SEARCH_FILTERS_DRAFT,
        jobTitles: initialFilters.jobTitle ? [initialFilters.jobTitle] : [],
        locations: initialFilters.location ? [initialFilters.location] : [],
        minExperience: initialFilters.yearsOfExperience ?? "",
        companyIndustries: initialFilters.industry ? [initialFilters.industry] : [],
        skills: initialFilters.skills ?? [],
      })
    }
  }, [initialFilters])

  const examplePrompts = [
    "Senior Python Developer in Toronto with 5+ years experience",
    "Frontend React Engineer at fintech startup",
    "DevOps Engineer with Kubernetes and AWS experience",
    "Full Stack Developer with Node.js and React skills",
    "Data Scientist with machine learning background",
    "Mobile Developer iOS Swift remote work",
    "Backend Engineer Java enterprise healthcare",
    "Security Engineer with penetration testing experience",
  ]

  const handleExampleClick = (example: string) => {
    setSearchQuery(example)
  }

  const handleSearch = () => {
    const query = searchQuery || jobDescription
    console.log({ query, booleanQuery, filters })
    if (onSearch) {
      onSearch(query, booleanQuery, filters)
    } else {
      // Default behavior - navigate to search results
      const params = new URLSearchParams({
        q: query,
        ...(booleanQuery && { boolean: booleanQuery }),
        ...(summaryItems.length > 0 && { filters: JSON.stringify(filters) }),
      })
      window.location.href = `/jobs/search?${params.toString()}`
    }
  }

  return (
    <div className="container mx-auto p-6 max-w-4xl">
      <div className="space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold tracking-tight">Find Your Next Hire</h1>
          <p className="text-muted-foreground">Use AI-powered search to find the perfect candidates for your role</p>
        </div>

        {/* Main Search Card */}
        <Card className="border-2 border-primary/20">
          <CardHeader>
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 text-brand-ink" />
              <CardTitle>Who are you looking for?</CardTitle>
            </div>
            <CardDescription>
              Describe your ideal candidate in natural language or paste a job description
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="natural">Natural Language</TabsTrigger>
                <TabsTrigger value="description">Job Description</TabsTrigger>
                <TabsTrigger value="boolean">Boolean Search</TabsTrigger>
              </TabsList>

              <TabsContent value="natural" className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="search-query">Search Query</Label>
                  <div className="relative">
                    <Input
                      id="search-query"
                      placeholder="e.g., Senior React developer with 5+ years experience in fintech"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pr-12"
                    />
                    <Button
                      size="sm"
                      className="absolute right-1 top-1 h-8 w-8 p-0"
                      onClick={handleSearch}
                      disabled={!searchQuery.trim()}
                    >
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                {/* Quick Filter Tags */}
                <div className="space-y-3">
                  <div className="flex flex-wrap gap-2">
                    {summaryItems.length > 0 ? (
                      summaryItems.map((item) => (
                        <Badge key={item.id} variant="default" className="bg-green-100 text-green-800 border-green-300">
                          <Check className="h-3 w-3 mr-1" />
                          {item.label}: {item.value}
                        </Badge>
                      ))
                    ) : (
                      <>
                        <Badge variant="outline" className="cursor-pointer hover:bg-primary/10">
                          <MapPin className="h-3 w-3 mr-1" />
                          Location
                        </Badge>
                        <Badge variant="outline" className="cursor-pointer hover:bg-primary/10">
                          <Briefcase className="h-3 w-3 mr-1" />
                          Job Title
                        </Badge>
                        <Badge variant="outline" className="cursor-pointer hover:bg-primary/10">
                          <Calendar className="h-3 w-3 mr-1" />
                          Years of Experience
                        </Badge>
                        <Badge variant="outline" className="cursor-pointer hover:bg-primary/10">
                          <Building className="h-3 w-3 mr-1" />
                          Industry
                        </Badge>
                        <Badge variant="outline" className="cursor-pointer hover:bg-primary/10">
                          <Code className="h-3 w-3 mr-1" />
                          Skills
                        </Badge>
                      </>
                    )}
                  </div>

                  {/* Analysis Status */}
                  {isAnalyzing && (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Sparkles className="h-4 w-4 animate-pulse" />
                      <span>AI is analyzing your query...</span>
                    </div>
                  )}

                  {/* Generated Query Preview */}
                  {booleanQuery && booleanQuery !== searchQuery && (
                    <div className="p-3 bg-muted rounded-lg">
                      <div className="text-sm font-medium text-muted-foreground mb-1">Generated Search Query:</div>
                      <code className="text-sm font-mono">{booleanQuery}</code>
                    </div>
                  )}

                  {/* Edit Filters Button */}
                  <div className="flex justify-end">
                    <EditFiltersDialog initialFilters={filters} onSubmit={setFilters} />
                  </div>
                </div>
              </TabsContent>

              {/* ... existing TabsContent for description and boolean ... */}
            </Tabs>
          </CardContent>
        </Card>

        {/* Example Prompts */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Try these examples:</CardTitle>
            <CardDescription>Click on any example to see how AI parses different queries</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-2">
              {examplePrompts.map((example, index) => (
                <Card
                  key={index}
                  className="cursor-pointer hover:shadow-md transition-shadow border-dashed"
                  onClick={() => handleExampleClick(example)}
                >
                  <CardContent className="p-4">
                    <p className="text-sm font-medium">{example}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
